
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Program } from '@/types/program';

export const useProgramManagement = (teamId?: string) => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('programs')
        .select('*')
        .order('created_at', { ascending: false });

      if (teamId) {
        query = query.eq('team_id', teamId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPrograms(data || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  const createProgram = async (programData: Omit<Program, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      
      const { data, error } = await supabase
        .from('programs')
        .insert([programData])
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Program created successfully');
      await fetchPrograms();
      return data;
    } catch (error) {
      console.error('Error creating program:', error);
      toast.error('Failed to create program');
      throw error;
    }
  };

  const updateProgram = async (id: string, updates: Partial<Program>) => {
    try {
      
      const { data, error } = await supabase
        .from('programs')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      toast.success('Program updated successfully');
      await fetchPrograms();
      return data;
    } catch (error) {
      console.error('Error updating program:', error);
      toast.error('Failed to update program');
      throw error;
    }
  };

  const deleteProgram = async (id: string) => {
    try {
      
      const { error } = await supabase
        .from('programs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Program deleted successfully');
      await fetchPrograms();
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error('Failed to delete program');
      throw error;
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  return {
    programs,
    loading,
    fetchPrograms,
    createProgram,
    updateProgram,
    deleteProgram
  };
};
